import { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ConciergeSidebar, signOut, useConciergeContext } from '@brandclub/common-ui';
import { useAppSelector } from '@/redux/hooks';
import { preloadChatHistory } from '@/components/routes/appPageRoutes';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import './concierge-tailwind.css';

const ConciergeOutlet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const conversationState = useConciergeContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);

  const { treatments } = useSplitTreatments({
    names: ['brandclub_dtc_concierge'],
    attributes: {
      email: userProfile?.email ?? '',
      domain: window.location.hostname,
    },
  });

  const isConciergeEnabled =
    treatments?.brandclub_dtc_concierge?.treatment === 'on' ||
    window.location.host.includes('localhost') ||
    window.location.href.includes('https://brandclub.com') ||
    window.location.href.includes('https://beta.brandclub.com') ||
    window.location.href.includes('https://gamma.brandclub.com') ||
    window.location.href.includes('https://www.brandclub.com');

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const sidebarUserProfile = useMemo(
    () => ({
      firstName: userProfile?.given_name,
      lastName: userProfile?.family_name,
      isSignedIn: !!userProfile?.userId,
    }),
    [userProfile],
  );

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      conversationState.setConversationHistory([]);
      conversationState.setFollowUpQuestions([]);
      conversationState.clearStreamingState();
      conversationState.setIsSharedView(false);
      conversationState.setSharedByUserName(undefined);
      conversationState.setSharedAtMessageIndex(undefined);
      conversationState.setSourceConversationId(undefined);
      conversationState.setSourceCheckpointIndex(undefined);
      conversationState.setSharedConversationId(null);
      conversationState.setConversationId(conversationId);
      navigate('/concierge');
    },
    [conversationState, navigate],
  );

  const handleComposeClick = useCallback(() => {
    conversationState.setConversationHistory([]);
    conversationState.setFollowUpQuestions([]);
    conversationState.setStreamingAIResponse('');
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(undefined);
    conversationState.setSharedAtMessageIndex(undefined);
    conversationState.setSourceConversationId(undefined);
    conversationState.setSourceCheckpointIndex(undefined);
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    navigate('/concierge');
  }, [conversationState, navigate]);

  const handleChatsClick = useCallback(() => {
    const isOnHistory = location.pathname.includes('/concierge/history');
    if (isOnHistory) {
      if (!isSidebarExpanded) setIsSidebarExpanded(true);
      return;
    }
    navigate('/concierge/history');
  }, [navigate, location.pathname, isSidebarExpanded]);

  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleProfileClick = useCallback(() => {
    navigate('/me');
  }, [navigate]);

  const handlePurchasesClick = useCallback(() => {
    navigate('/purchase');
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('conversationId');
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    await signOut();
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    preloadChatHistory();
  }, []);

  if (!isConciergeEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="concierge-root"
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100dvh',
        minHeight: '-webkit-fill-available',
      }}
    >
      <ConciergeSidebar
        onConversationSelect={handleConversationSelect}
        onComposeClick={handleComposeClick}
        onChatsClick={handleChatsClick}
        currentConversationId={conversationState.conversationId}
        userProfile={sidebarUserProfile}
        apiBaseUrl={conversationState.apiBaseUrl}
        onSignOutClick={handleSignOut}
        onProfileClick={handleProfileClick}
        onPurchasesClick={handlePurchasesClick}
        onHomeClick={handleHomeClick}
        isExpanded={isSidebarExpanded}
        onToggle={handleSidebarToggle}
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};

export default ConciergeOutlet;
